/**
 * What is left of the dashboard module.
 *
 * index.html carried `#dashLegacy`: 315 lines, 98 ids, display:none, unreachable by any code
 * path in src/. Twenty-seven functions here painted into it on every render — 1,777 lines, 58%
 * of this file. The markup went first (2026-08-03); the functions went with this commit.
 *
 * A correction, because the first commit stated it wrongly. That message claimed
 * `generateAIInsights` and `_dashFxImpact` wrote S and that `generateMarketIntel` also fetched,
 * and used that as the reason to keep them. Measured afterwards, none of it was true: all
 * twenty-seven read S and wrote DOM, nothing else. The claim came from an earlier analysis that
 * was repeated instead of re-run. Keeping them was still the right call for that commit — a
 * removal has to be reversible one step at a time — but it was right for a reason that had not
 * been checked.
 *
 * One real dependency existed, and it survives: `_dashActions` was a wrapper that painted a dead
 * list and then called `renderActionBell`. renderDash now calls the bell directly.
 */
import { S, save, _lastTrims, _IMPORT_KEYS, MERGE_ARRAYS } from '../core/state.js';
import { hVal, hPnl, hTotUSD, hWealthUSD, getMs, tdAccrued } from '../core/math.js';
import { calcPortfolioXIRR } from '../modules/portfolio.js';
import { esc, f, fU, fT, fP, fByCur, pc, toUSD, toUSDAt, ml } from '../core/utils.js';
import { _safeChart, _chartUpdate, _safeDestroy } from '../ui/charts.js';
import { PS } from '../core/prices.js';
import { driftHoldings, effectiveStop, netWorthUSD, _nwProviderWired } from '../core/risk-calc.js';
// SPK was reached through `window.SPK`, set by main.js. That works in the browser and nowhere
// else: the motion kit is a module with a named export, so a bare global was a dependency the
// module never declared and no test could satisfy.
import { SPK } from '../ui/spk.js';

/**
 * Canonical net-worth (USD) — single source of truth, pure and order-independent.
 * Identical formula to renderDash's twUSD; consumed by renderDash and (via registration)
 * by core/risk-calc so the Risk page never depends on render order. audit H-3/H-4.
 */
export function computeTotalWealthUSD() {
  let gVal = 0, gInvNoPrice = 0;
  S.holdings.forEach(h => { const w = hWealthUSD(h); if (w.atCost) gInvNoPrice += w.usd; else gVal += w.usd; });
  let tPnL = 0;
  S.trades.filter(t => t.status !== 'Closed').forEach(t => { const p = S.prices[t.ticker]; if (!p) return;
    tPnL += toUSD((t.side === 'Long' ? p.price - t.entry : t.entry - p.price) * t.qty * t.leverage, t.currency || 'USD'); });
  // LEDGER, not cashTRYNow(). Total Wealth is one side of a capital identity: the other side,
  // netExtCapital, only learns about household spending when reconcileCashTRY books the drift
  // as external_out. Moving this side alone made `twPnL = twUSD - netExtCapital` report the
  // week's card payments as an INVESTMENT LOSS, and the reconciliation that followed as an
  // equal phantom gain — both of which then entered the snapshot series as real daily returns
  // feeding volatility, max drawdown and the benchmark comparison. Liquidity is the Cash
  // page's question and it is answered there; this figure answers a different one.
  // (audit 2026-08-10, round 2)
  const totalCashUSD = (S.cash?.USD || 0) + toUSD(S.cash?.TRY || 0, 'TRY') + toUSD(S.cash?.EUR || 0, 'EUR');  // ledger-basis: capital identity, deliberate — see computeTotalWealthUSD
  let tradeInvHero = 0; S.trades.filter(t => t.status !== 'Closed').forEach(t => { tradeInvHero += toUSD(t.entry * t.qty, t.currency || 'USD'); });
  let tdAccruedUSD = 0;
  S.trades.filter(t => t.status !== 'Closed' && t.type === 'TimeDeposit' && t.td).forEach(t => {
    const acc = tdAccrued(t.td);
    if (acc && acc.currentValue) tdAccruedUSD += toUSD(acc.currentValue, t.currency || 'TRY') - toUSD(t.td.principal || 0, t.currency || 'TRY');
  });
  let rfUSD = 0;
  if (typeof S.reserveFund === 'object') {
    Object.entries(S.reserveFund).forEach(([c, v]) => { if (!v) return; const tryVal = rfToTRY(v, c); rfUSD += S.fxRate ? tryVal / S.fxRate : 0; });
  } else { rfUSD = S.fxRate ? (S.reserveFund || 0) / S.fxRate : 0; }
  const liabUSD = totalLiabilitiesUSD();
  return gVal + gInvNoPrice + totalCashUSD + tradeInvHero + tPnL + tdAccruedUSD + rfUSD - liabUSD;
}

/**
 * netGrowthUSD() — NET GROWTH (total-wealth P&L vs net external capital), extracted from
 * renderDash so cross-page consumers (Portfolio hero KPI) call it LIVE instead of reading
 * a window global that goes stale when the dashboard hasn't re-rendered (cold-boot finding
 * 2026-07-14: __twUSD/__twPnL froze at default-state values until Dashboard was visited).
 * Returns null when there is no positive external-capital history — renderDash then falls
 * back to gP+tPnL locally; other consumers show '—'.
 */
/**
 * The conversion context capital.js runs on. Metal-aware, because the reserve fund holds
 * 15.09g of gold and 111.27g of silver and a converter that returns NaN for XAU would erase
 * them; `costOfHolding` is hTotUSD so there is exactly one cost basis in the app.
 */
export function capitalCtx() {
  // ⚠ `NaN`, NOT `null`, WAS THE ANSWER HERE — and UI-004 filed that as a third rendering.
  // MEASURED 2026-08-17: `f()` and `fU()` both test `n == null || isNaN(n)`, so **NaN and null
  // render the SAME em dash**. The display was never inconsistent; the VALUE was. NaN propagates
  // silently through any arithmetic downstream (`NaN + x` is NaN, and no guard notices), while
  // null is caught by the `== null` half of every formatter and by `Number(x) || 0` at the few
  // sites that coerce. Same pixel, safer value.
  const metal = (v, cur) => (S.fxRate > 0 ? rfToTRY(v, cur) / S.fxRate : null);
  const conv = (v, cur) => {
    const c = cur || 'USD';
    if (c === 'USD') return +v || 0;
    if (c === 'TRY' || c === 'EUR' || c === 'GBP' || c === 'XAU' || c === 'XAG') return metal(+v || 0, c);
    return NaN;
  };
  // USD per unit of `cur` on `dateISO`, from the app's OWN rate history, or null. null
  // means "no record", and every caller must treat that as uncomputable rather than
  // substituting today's rate — substituting spot reports an FX effect of exactly zero,
  // which is the most flattering answer available. (audit 2026-08-02)
  //
  // Hoisted out of the returned literal so `toUSDAt` below can consult it. It could not,
  // and that was ACCT-004.
  const rateAt = (dateISO, cur) => {
    const r = S.monthlyRates && S.monthlyRates[String(dateISO || '').slice(0, 7)];
    if (!r || !(r.usdtry > 0)) return null;
    if (cur === 'TRY') return 1 / r.usdtry;
    if (cur === 'EUR') return r.eurtry > 0 ? r.eurtry / r.usdtry : null;
    // Metals are stored as USD per GRAM, which is the unit the holdings and the reserve
    // fund are measured in — no conversion, no ounce/gram trap. (audit 2026-08-03)
    if (cur === 'XAU') return r.xauUSD > 0 ? r.xauUSD : null;
    if (cur === 'XAG') return r.xagUSD > 0 ? r.xagUSD : null;
    return null;   // still no stored history for GBP
  };
  return {
    toUSD: conv,
    rateAt,
    // Event-time where the app can date it, spot otherwise.
    //
    // ⚠ ACCT-004 — METALS WERE VALUED AT SPOT ON BOTH SIDES OF THE IDENTITY.
    //
    // This line used to read `(cur === 'XAU' || cur === 'XAG') ? conv(v, cur) : …`, excused as
    // "a known approximation, not a silent one". It was silent, and it was not necessary: the
    // rate history four lines above HAS the metal price for the month the metal arrived, and
    // `backfillMonthlyRates` writes `xauUSD`/`xagUSD` for every month it can reach.
    //
    // The reserve holds 15.09g of gold and 111.27g of silver, both deposited 2026-05-02.
    // Booking them at TODAY's price on the capital side, while net worth also marks them at
    // today's price, makes their entire price movement cancel to exactly zero P&L — the
    // failure mode the `rateAt` comment above names in as many words, happening one line
    // below it.
    //
    // Measured 2026-08-16: gold 146.62 → 140.68 USD/g, silver 2.4311 → 2.0933 USD/g. Booked
    // at spot the two rows contribute $2,355.78 of "capital contributed"; at the rate of the
    // month they actually arrived, $2,483.06. **$127.28 of reserve loss was invisible**, and
    // net-worth P&L was overstated by the same amount.
    //
    // (The register carried this as ~$450–500 of *appreciation* wrongly booked as capital.
    // Both the size and the SIGN were wrong — metals are down since May, so the defect was
    // hiding a loss, not a gain. Measured, not inherited.)
    //
    // A month with no metal rate still falls back to spot rather than returning null: a
    // converter that yields NaN for XAU erases the reserve entirely, which is the bug this
    // context was built to prevent (see the header above).
    toUSDAt: (v, cur, d) => {
      if (cur === 'XAU' || cur === 'XAG') {
        const r = rateAt(d, cur);
        return r > 0 ? (+v || 0) * r : conv(v, cur);
      }
      return toUSDAt(v, cur || 'TRY', d);
    },
    costOfHolding: h => hTotUSD(h),
  };
}

export function netGrowthUSD() {
  // Capital is measured by capital.js: an ALLOWLIST across all five doors money enters
  // through, with anything unclassifiable reported rather than counted. The note-denylist
  // this replaced booked a $17,273 time-deposit opening and an unlabelled ₺500,000 row as
  // external capital and subtracted a $12,974 bookkeeping correction from it, which is how
  // "+43.5% growth" appeared against roughly $2,000 of nameable P&L. (audit 2026-08-02)
  const rec = reconcileCapital(S, capitalCtx());
  const twUSD = computeTotalWealthUSD();

  // Unreconciled means the denominator is not the whole capital. There is no honest
  // percentage to print, so none is returned — the reasons are, so the UI can say why.
  if (!rec.reconciled) {
    return { twUSD, netExtCapital: null, twPnL: null, twPP: null, reconciled: false,
      reasons: rec.reasons, unknown: rec.unknown, doors: rec.doors, residualUSD: rec.residualUSD };
  }
  const netExtCapital = rec.externalUSD;
  if (!(netExtCapital > 0)) {
    return { twUSD, netExtCapital: null, twPnL: null, twPP: null, reconciled: false,
      reasons: ['no positive external capital recorded'], unknown: rec.unknown, doors: rec.doors, residualUSD: rec.residualUSD };
  }
  const twPnL = twUSD - netExtCapital;
  return { twUSD, netExtCapital, twPnL, twPP: (twPnL / netExtCapital) * 100,
    reconciled: true, reasons: [], unknown: [], doors: rec.doors, residualUSD: rec.residualUSD };
}
import { reserveTargetUSD, monthOutflowTRY, cashTotalUSD, cashTRYNow } from '../modules/cashflow.js';
import { rfToTRY, reserveCoverageUSD } from '../modules/cash.js';
import { totalLiabilitiesUSD } from '../modules/liabilities.js';
import { renderDigestCard } from '../modules/ai-digest.js';
import { checkDCALock } from '../modules/rebalancing.js';
import { scanDCAOpportunities } from '../modules/ai-copilot.js';
import { integrityIssues } from '../core/integrity.js';
import { evaluateTrigger } from '../core/portfolio-policy.js';
import { lotEchoes } from '../core/month-lots.js';
import { evaluateGates, GATES } from '../core/cycle-phase.js';
import { taxRefundsPaid } from '../core/tax-bridge.js';
import { statementsSeen } from '../core/statement-bridge.js';
import { reconcileCapital } from '../core/capital.js';
import { fxEffectOnCost, datedCostBasis } from '../core/fx-effect.js';
import { unknownFeeHoldings, unknownFeeTrades } from '../core/fees.js';
import { backfillMonthlyRates, monthsOfCost } from '../core/fx-history.js';
import { backfillBenchmarkPrices } from '../core/benchmark-history.js';
import { toast } from '../ui/layout.js';
import { cloudSave } from '../core/persistence.js';

// SVG icon system — replaces emojis for consistent visual language
const _s = (d, c = 'currentColor') => `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="${c}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:middle;flex-shrink:0">${d}</svg>`;
const IC = {
  warn:    (c) => _s('<path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>', c),
  chart:   (c) => _s('<line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>', c),
  search:  (c) => _s('<circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>', c),
  box:     (c) => _s('<path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>', c),
  down:    (c) => _s('<polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/>', c),
  zap:     (c) => _s('<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>', c),
  cart:    (c) => _s('<circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 002 1.61h9.72a2 2 0 002-1.61L23 6H6"/>', c),
  clock:   (c) => _s('<circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>', c),
  signal:  (c) => _s('<path d="M2 20h.01"/><path d="M7 20v-4"/><path d="M12 20v-8"/><path d="M17 20V8"/>', c),
  edit:    (c) => _s('<path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>', c),
  alert:   (c) => _s('<circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>', c),
  boom:    (c) => _s('<polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>', c),
  target:  (c) => _s('<circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/>', c),
  save:    (c) => _s('<path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/><polyline points="17 21 17 13 7 13 7 21"/><polyline points="7 3 7 8 15 8"/>', c),
  budget:  (c) => _s('<line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>', c),
  balance: (c) => _s('<rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="9" y1="21" x2="9" y2="9"/>', c),
  trophy:  (c) => _s('<path d="M6 9H4.5a2.5 2.5 0 010-5H6"/><path d="M18 9h1.5a2.5 2.5 0 000-5H18"/><path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20 7 22"/><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20 17 22"/><path d="M18 2H6v7a6 6 0 0012 0V2z"/>', c),
  shuffle: (c) => _s('<polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/><line x1="4" y1="4" x2="9" y2="9"/>', c),
  dollar:  (c) => _s('<line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6"/>', c),
  drop:    (c) => _s('<polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/>', c),
  money:   (c) => _s('<rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>', c),
};

// Global chart references


// ═══════ HEATMAP TREEMAP ═══════
let _heatmapMode = 'pnl'; // 'pnl' | 'weight' | 'value'

export function setHeatmapMode(mode) {
  _heatmapMode = mode;
  // The neo `.seg` marks its selection with 'on'. 'active' belonged to the legacy control and
  // styled nothing here, which is why the buttons never looked pressed either.
  document.querySelectorAll('[data-action="setHeatmapMode"]').forEach(b => {
    b.classList.toggle('on', (b.dataset.args || '').includes(mode));
  });
  _neoHeatmap(false);
}

/**
 * The Portfolio Map, and the three buttons above it.
 *
 * P&L % / Weight / Value were wired to `_dashHeatmap()`, which painted `#heatmapGrid` — an id
 * inside the legacy block. The map on screen is `#neoHeat`, drawn by SPK.heatmap, and it never
 * read `_heatmapMode`. So the segment was decorative: it set a variable, toggled a class the
 * neo `.seg` does not use ('active' instead of 'on'), and repainted something nobody could see.
 * Found while removing the legacy markup — the control had been dead longer than that.
 *
 * Size and colour stay where the caption says they are: area is position value, colour is P&L.
 * Only the number on the tile follows the mode.
 */
function _neoHeatmap(animate) {
  const el = document.getElementById('neoHeat');
  if (!el) return;
  const items = S.holdings.map(h => {
    const v = hVal(h), inv = hTotUSD(h);
    return { t: h.ticker, value: v.vU || 0, pnl: inv > 0 ? +(((v.vU - inv) / inv) * 100).toFixed(1) : 0 };
  }).filter(i => i.value > 0);
  if (!items.length) return;
  const total = items.reduce((a, i) => a + i.value, 0);
  const label = _heatmapMode === 'weight'
    ? (it => total > 0 ? (it.value / total * 100).toFixed(1) + '%' : '—')
    : _heatmapMode === 'value'
      ? (it => fU(it.value))
      : null;   // 'pnl' keeps SPK's own default
  SPK.heatmap(el, items, { animate: !!animate, label });
}


// last-seen price per mover ticker, so the live board flashes only on a REAL price change
const _moverLastPx = {};


/**
 * The action items the notification bell shows. Pure: reads S, no writes, no network.
 */
export function computeActionItems() {
  const actions = [];
  // 0. INTEGRITY WATCHDOG (audit 2026-08-01). Anything that cannot prove its
  //    freshness becomes a to-do here rather than a console line nobody reads.
  //    integrityIssues() is pure: no state writes, no network, no LLM.
  //    prices.* is skipped — items 3 and 4 below already cover it.
  // Capital reconciliation is computed here and handed IN, because integrity.js imports
  // nothing and capital.js needs FX plus a cost basis. A failure to compute it must not
  // silence the rest of the watchdog, so it degrades to UNKNOWN rather than throwing.
  let _capOpts = null;
  try {
    const _cc = capitalCtx();
    _capOpts = { capital: reconcileCapital(S, _cc), fx: fxEffectOnCost(S, _cc), fees: unknownFeeHoldings(S.holdings), feeTrades: unknownFeeTrades(S.trades), trims: _lastTrims };
    // The bell reads the same checks as the page, so it must inject the same inputs — an input
    // only one of the two supplies leaves a permanent amber row occupying a slot in a four-item
    // list. `computeTotalWealthUSD` is local to this file; risk-calc is already imported here.
    _capOpts.netWorth = { canonical: computeTotalWealthUSD(), viaRiskCalc: netWorthUSD(), providerWired: _nwProviderWired() };
  } catch (_e) { _capOpts = null; }
  // Paid tax refunds ride in the same injection channel, and for the same reason: integrity.js
  // imports nothing, and these live in the tax module's own IDB store rather than in S.
  // null (not []) when the snapshots have not been read — "nobody looked" is not "all clear".
  const _tr = taxRefundsPaid();
  if (_tr) _capOpts = Object.assign({}, _capOpts || {}, { taxRefunds: _tr });
  // The newest statement per card, for `cashflow.statementMismatch`; same channel, same
  // null-is-not-empty rule (statement-bridge.js).
  const _st = statementsSeen();
  if (_st) _capOpts = Object.assign({}, _capOpts || {}, { statements: _st });
  // Same channel, same reason: `portfolio.accumulateTrigger` must not sit as a permanent
  // UNKNOWN in the bell because only the Integrity page injected its comparator.
  _capOpts = Object.assign({}, _capOpts || {}, { evaluateTrigger, lotEchoes, cycleGates: evaluateGates, cycleInvalidations: GATES.invalidations });
  // The bell reads the same report as the page, so it has to be handed the same facts — or
  // `import.keyLoss` sits as a permanent UNKNOWN occupying a slot in the four-item action bell.
  _capOpts = Object.assign({}, _capOpts || {}, { importKeys: _IMPORT_KEYS, mergeArrays: MERGE_ARRAYS });
  try {
    integrityIssues(S, Date.now(), _capOpts)
      .filter(c => c.id !== 'prices.stale' && c.id !== 'prices.missing')
      .slice(0, 4)
      .forEach(c => {
        const col = c.status === 'critical' ? 'var(--red)' : c.status === 'unknown' ? 'var(--ora)' : 'var(--yel)';
        actions.push({
          icon: IC.alert(col),
          text: `${c.label}: ${c.detail}${c.consequence ? ' — ' + c.consequence : ''}`
            // The one check whose remedy is a single click rather than a page to visit.
            + (c.id === 'fx.costBasisDates'
              ? ` <button class="btn btn-ghost" data-action="backfillFxRates" style="font-size:10px;padding:3px 8px;margin-left:6px">Fetch the missing rates</button>` : ''),
          color: col,
          action: c.page || '',
        });
      });
  } catch (e) {
    // A watchdog that fails silently is the bug it exists to prevent.
    actions.push({ icon: IC.alert('var(--red)'), text: `Integrity watchdog failed: ${e.message}`, color: 'var(--red)', action: '' });
  }
  try { window.__spektrxIntegrity = integrityIssues(S, Date.now(), _capOpts); window.__spektrxCapital = _capOpts && _capOpts.capital; window.__spektrxFx = _capOpts && _capOpts.fx; } catch (_e) { /* non-browser */ }
  const today = new Date(); const dayOfMonth = today.getDate();
  // 1. Buy window open today
  const bwOpen = S.holdings.filter(h => { if (!h.buyWindow) return false; const [s, e] = h.buyWindow.split('-').map(Number);
    return dayOfMonth >= s && dayOfMonth <= e; });
  if (bwOpen.length) actions.push({ icon: IC.cart('var(--grn)'), text: `${bwOpen.length} holdings in buy window today (${bwOpen.slice(0, 4).map(h => h.ticker).join(', ')}${bwOpen.length > 4 ? '...' : ''})`, color: 'var(--grn)', action: 'monthly' });
  // 2. Partial months — nag only for the current or past months (future months aren't due yet),
  //    and denominate by the monthly PLAN size (holdings with an allocation), not all holdings.
  const curMonthKey = today.toISOString().slice(0, 7); // YYYY-MM
  const planSize = S.holdings.filter(h => (h.allocationPct || 0) > 0).length || S.holdings.length;
  const partialMs = Object.entries(S.monthlyRates).filter(([m, r]) => r.status === 'partial' && m <= curMonthKey);
  partialMs.forEach(([m, r]) => { const eCnt = r.entries ? Object.keys(r.entries).length : 0;
    actions.push({ icon: IC.clock('var(--yel)'), text: `${ml(m)}: ${eCnt}/${planSize} entries — needs completion`, color: 'var(--yel)', action: `openAddMo('${m}')` }); });
  // 3. Stale prices (>24h)
  const staleH = 24 * 60 * 60 * 1000; const staleP = [];
  S.holdings.forEach(h => { const p = S.prices[h.ticker]; if (!p) return; if (Date.now() - p.ts > staleH) staleP.push(h.ticker); });
  S.trades.filter(t => t.status !== 'Closed').forEach(t => { const p = S.prices[t.ticker]; if (!p) return; if (Date.now() - p.ts > staleH && !staleP.includes(t.ticker)) staleP.push(t.ticker); });
  if (staleP.length) actions.push({ icon: IC.signal('var(--ora)'), text: `${staleP.length} stale prices (>24h) — refresh or enter manually`, color: 'var(--ora)', action: 'settings' });
  // 4. Manual price needed
  const manPr = S.holdings.filter(h => PS[h.ticker]?.s === 'm' && !S.prices[h.ticker]);
  if (manPr.length) actions.push({ icon: IC.edit('var(--yel)'), text: `${manPr.length} instruments need manual price entry`, color: 'var(--yel)', action: 'settings' });
  // 5. SL proximity (<5%)
  S.trades.filter(t => t.status !== 'Closed' && t.sl).forEach(t => { const p = S.prices[t.ticker]; if (!p) return;
    const dist = t.side === 'Long' ? (p.price - t.sl) / p.price * 100 : (t.sl - p.price) / p.price * 100;
    if (dist < 5 && dist > 0) actions.push({ icon: IC.alert('var(--red)'), text: `${t.ticker} is ${dist.toFixed(1)}% from SL (${fByCur(t.currency || 'USD', t.sl)})`, color: 'var(--red)', action: '' });
    if (dist <= 0) actions.push({ icon: IC.boom('var(--red)'), text: `${t.ticker} has breached SL!`, color: 'var(--red)', action: '' }); });
  // 6. TP1 proximity (<3%)
  S.trades.filter(t => t.status !== 'Closed' && t.tp1).forEach(t => { const p = S.prices[t.ticker]; if (!p) return;
    const dist = t.side === 'Long' ? (t.tp1 - p.price) / p.price * 100 : (p.price - t.tp1) / p.price * 100;
    if (dist < 3 && dist > 0) actions.push({ icon: IC.target('var(--grn)'), text: `${t.ticker} is ${dist.toFixed(1)}% from TP1 (${fByCur(t.currency || 'USD', t.tp1)})`, color: 'var(--grn)', action: '' }); });
  // 7. Backup overdue
  const bkDays = S.settings.backupDays || 7;
  if (!S.lastBackup || ((Date.now() - new Date(S.lastBackup).getTime()) / (86400000)) > bkDays) actions.push({ icon: IC.save('var(--yel)'), text: 'JSON backup overdue — export now', color: 'var(--yel)', action: 'settings' });
  // 8. Budget check
  if (S.budgets?.monthlyLimit > 0) {
    const curK = new Date().getFullYear() + '-' + String(new Date().getMonth() + 1).padStart(2, '0');
    const mo = S.cashflow[curK];
    if (mo) {
      // Karma excluded — a budget cannot be blown by a bill somebody else pays.
      const spent = monthOutflowTRY(mo, true);
      const pct = S.budgets.monthlyLimit > 0 ? (spent / S.budgets.monthlyLimit) * 100 : 0;
      if (pct >= 100) actions.push({ icon: IC.budget('var(--red)'), text: `Budget exceeded: ${fT(spent)} / ${fT(S.budgets.monthlyLimit)} (${f(pct, 0)}%)`, color: 'var(--red)', action: 'cashflow' });
      else if (pct >= 80) actions.push({ icon: IC.budget('var(--yel)'), text: `Budget at ${f(pct, 0)}%: ${fT(spent)} / ${fT(S.budgets.monthlyLimit)}`, color: 'var(--yel)', action: 'cashflow' });
    }
  }
  // 9. Rebalancing drift — Retirement-scoped (only sleeve with allocationPct targets; 2026-07-24 fix)
  const rebalTh = parseFloat(S.settings?.rebalThreshold) || 5;
  const dashDriftH = driftHoldings();
  const dashTotalVal = dashDriftH.reduce((s, h) => { const v = hVal(h); return s + (v.vU || 0); }, 0);
  if (dashTotalVal > 0) { const dashTA = {}; dashDriftH.forEach(h => { dashTA[h.type] = (dashTA[h.type] || 0) + h.allocationPct; });
    const dashTD = {}; dashDriftH.forEach(h => { if (!dashTD[h.type]) dashTD[h.type] = 0; const v = hVal(h); if (v.vU) dashTD[h.type] += v.vU; });
    const drifts = Object.entries(dashTA).filter(([tp, tgt]) => { const act = dashTD[tp] ? (dashTD[tp] / dashTotalVal) * 100 : 0; return Math.abs(act - tgt) > rebalTh; })
      .map(([tp, tgt]) => { const act = dashTD[tp] ? (dashTD[tp] / dashTotalVal) * 100 : 0; return { tp, dev: act - tgt }; });
    if (drifts.length) actions.push({ icon: IC.balance('var(--ora)'), text: `${drifts.length} asset type(s) drifted >${rebalTh}% from target (${drifts.map(d => `${d.tp} ${d.dev > 0 ? '+' : ''}${d.dev.toFixed(1)}%`).join(', ')})`, color: 'var(--ora)', action: 'portfolio' }); }

  return actions;
}


// ===== Action-Needed top-bar bell (neon, global, expand-on-click) =====
export function renderActionBell(items) {
  const actions = items || computeActionItems();
  const n = document.getElementById('actBellN');
  const list = document.getElementById('actBellList');
  if (n) { n.textContent = actions.length ? String(actions.length) : ''; n.style.display = actions.length ? 'flex' : 'none'; }
  if (!list) return;
  list.innerHTML = actions.length
    ? actions.map(a => { const nav = a.action ? (a.action.includes('(') ? 'monthly' : a.action) : ''; return `<div class="ab-row${nav ? ' nav' : ''}"${nav ? ` data-action="navTo" data-args='["${nav}"]'` : ''}><span class="ab-dot" style="background:${a.color}"></span><span class="ab-txt">${a.text}</span></div>`; }).join('')
    : '<div class="ab-empty">All clear — nothing needs attention right now.</div>';
}

// ===== Dashboard ticker-tape (scrolling holdings, color-coded P&L%) =====
function renderTape() {
  const wrap = document.getElementById('dashTape');
  const track = document.getElementById('dashTapeTrack');
  if (!wrap || !track) return;
  const items = S.holdings
    .map(h => { const v = hVal(h); if (!v.vU) return null; const p = hPnl(h); return { tk: h.ticker, pct: p.pnlP || 0 }; })
    .filter(Boolean)
    .sort((a, b) => b.pct - a.pct);
  if (!items.length) { wrap.style.display = 'none'; return; }
  wrap.style.display = '';
  const lab = i => `${i.pct >= 0 ? '▲' : '▼'} ${Math.abs(i.pct).toFixed(2)}%`;
  const sig = items.map(i => i.tk).join(',');
  if (window._tapeSig !== sig) {
    window._tapeSig = sig;
    const cell = i => `<span class="tape-item"><b>${i.tk}</b><span class="tape-pc ${i.pct >= 0 ? 'up' : 'dn'}" data-tk="${i.tk}">${lab(i)}</span></span>`;
    const html = items.map(cell).join('');
    track.innerHTML = html + html; // duplicate for seamless -50% loop
  } else {
    items.forEach(i => track.querySelectorAll(`.tape-pc[data-tk="${i.tk}"]`).forEach(el => { el.textContent = lab(i); el.className = 'tape-pc ' + (i.pct >= 0 ? 'up' : 'dn'); }));
  }
}


// Timeline range filter (default matches the 1M highlighted in the hero .seg control)
let _tlRange = '1M';

export function setTimelineRange(range) {
  _tlRange = range;
  // Update active state on BOTH control styles: hero .seg <span> (class "on") and legacy .tl-range-btn (class "active")
  document.querySelectorAll('[data-action="setTimelineRange"]').forEach(b => {
    let rArg = b.textContent.trim();
    try { const a = JSON.parse(b.getAttribute('data-args') || '[]'); if (a[0]) rArg = a[0]; } catch (_e) { /* keep text */ }
    const match = rArg === range;
    b.classList.toggle('on', match);
    b.classList.toggle('active', match);
  });
  // Redraw the hero Total Wealth SVG (guarded _neoBuilt prevents renderDash from doing it)
  drawWealthChart();
  renderDash();
}

function filterSnapsByRange(snaps) {
  if (_tlRange === 'ALL' || !snaps.length) return snaps;
  const now = new Date();
  const rangeDays = { '1D': 1, '1W': 7, '1M': 30, '3M': 90, '6M': 180, '1Y': 365 };
  const days = rangeDays[_tlRange] || 9999;
  const cutoff = new Date(now.getTime() - days * 86400000);
  const cutStr = cutoff.toISOString().slice(0, 10);
  return snaps.filter(s => s.date >= cutStr);
}

// Hero Total Wealth area chart (SVG #neoChart). Reads the real snapshot field (totalWealth),
// honors the selected range, and never falls back to random sample data when real history exists.
function drawWealthChart(animate = true) {
  const el = document.getElementById('neoChart');
  if (!el || !SPK || !SPK.areaChart) return;
  const filtered = filterSnapsByRange(S.snapshots || []);
  const pts = filtered
    .map(s => (typeof s.totalWealth === 'number' ? s.totalWealth
      : (s.twUSD ?? s.totalUSD ?? s.value ?? s.net)))
    .filter(v => typeof v === 'number' && v > 0);
  if (pts.length >= 2) {
    SPK.areaChart(el, pts, { w: 820, h: 210, animate });
  } else if (pts.length === 1) {
    SPK.areaChart(el, [pts[0], pts[0]], { w: 820, h: 210, animate }); // flat line for a single point
  } else {
    el.innerHTML = ''; // real data exists elsewhere but none in range — clear rather than show fake
  }
}

// Collapsible dashboard section state
// Tier 2 (default open): dsOverview, dsAlloc
// Tier 3 (default closed): dsRisk, dsMktIntel
const _sectionState = { dsOverview: false, dsAlloc: false, dsRisk: true, dsMktIntel: true };

export function toggleDashSection(id) {
  _sectionState[id] = !_sectionState[id];
  const body = document.getElementById(id + 'Body');
  const arrow = document.getElementById(id + 'Arrow');
  if (body) body.classList.toggle('collapsed', _sectionState[id]);
  if (arrow) arrow.classList.toggle('collapsed', _sectionState[id]);
}


let _aiCollapsed = false;


/** Simple time-ago helper */
function _timeAgo(dateStr) {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    const mins = Math.floor((Date.now() - d.getTime()) / 60000);
    if (mins < 1) return 'now';
    if (mins < 60) return mins + 'm ago';
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return hrs + 'h ago';
    const days = Math.floor(hrs / 24);
    return days + 'd ago';
  } catch (_e) { return ''; }
}

/**
 * _dashSnapshot(D)
 * Records daily snapshot for historical tracking
 */

/**
 * Fetch the monthly benchmark closes the DCA comparison is missing.
 *
 * Explicitly user-triggered: a render path that fetches and writes state is how surprise
 * network calls and surprise cloud writes happen. Only the months that are actually
 * missing are stored, so the blob grows by one float per contribution month.
 * (audit 2026-08-01)
 */
export async function backfillBenchmark() {
  const btns = document.querySelectorAll('[data-action="backfillBenchmark"]');
  btns.forEach(b => { b.disabled = true; b.textContent = 'Fetching…'; });
  try {
    const months = getMs();
    const res = await backfillBenchmarkPrices(S, months, 'VOO');
    if (!res.ok) {
      toast('Could not fetch benchmark history: ' + (res.error || 'unknown') + '. Nothing was written.', 'a-r', 8000);
    } else if (res.added > 0) {
      save();
      // save() only SCHEDULES a cloud write, 15s out. A one-shot user action that fetches
      // data must not depend on that timer: reload inside the window and load() takes the
      // cloud copy, which does not have the write yet — and then persists that copy back
      // over the local one, erasing it from every store. Observed twice with this exact
      // backfill. Flush now and report what actually landed. (audit 2026-08-01)
      let synced = false;
      try { synced = await cloudSave(); } catch (_e) { synced = false; }
      toast(
        `Backfilled ${res.added} monthly VOO close${res.added > 1 ? 's' : ''}`
        + `${res.stillMissing.length ? ` · ${res.stillMissing.length} still unavailable upstream` : ''}`
        + `${synced ? '' : ' · saved on this device, cloud sync pending — avoid reloading until the badge reads synced'}`,
        synced ? 'a-g' : 'a-y', synced ? 7000 : 11000);
    } else {
      toast('Nothing to backfill — every contribution month already has a price.', 'a-b');
    }
  } catch (e) {
    toast('Backfill failed: ' + (e && e.message), 'a-r', 7000);
  } finally {
    renderDash();
  }
}

/**
 * Fetch the historical USD/TRY and EUR/TRY closes the currency-effect calculation is missing.
 *
 * Live, five SAP lots from 2025 could not be priced at all — $7,572 of cost basis, 12% of the
 * total — because monthlyRates began at 2026-01. Their dates were never in doubt; the rates
 * were. Same one-shot, user-triggered, flush-before-reload contract as the benchmark
 * backfill, for the same reason: save() only SCHEDULES a cloud write 15s out, and a reload
 * inside that window takes the cloud copy and then persists it back over the local one.
 * (audit 2026-08-02)
 */
export async function backfillFxRates() {
  const btns = document.querySelectorAll('[data-action="backfillFxRates"]');
  btns.forEach(b => { b.disabled = true; b.textContent = 'Fetching…'; });
  try {
    const months = monthsOfCost(datedCostBasis(S).dated);
    const res = await backfillMonthlyRates(S, months);
    if (!res.ok) {
      toast('Could not fetch FX history: ' + (res.error || 'unknown') + '. Nothing was written.', 'a-r', 8000);
    } else if (res.added > 0) {
      save();
      let synced = false;
      try { synced = await cloudSave(); } catch (_e) { synced = false; }
      toast(
        `Backfilled ${res.added} month${res.added > 1 ? 's' : ''} of FX history (${res.months[0]} → ${res.months[res.months.length - 1]})`
        + `${res.stillMissing.length ? ` · ${res.stillMissing.length} still unavailable upstream` : ''}`
        + `${synced ? '' : ' · saved on this device, cloud sync pending — avoid reloading until the badge reads synced'}`,
        synced ? 'a-g' : 'a-y', synced ? 7000 : 11000);
    } else {
      toast('Nothing to backfill — every month carrying a purchase already has a rate.', 'a-b');
    }
  } catch (e) {
    toast('FX backfill failed: ' + (e && e.message), 'a-r', 7000);
  } finally {
    renderDash();
  }
}

export function _dashSnapshot(D) {
  if (D.hv) {
    const today = new Date().toISOString().slice(0, 10);
    if (!S.snapshots) S.snapshots = [];
    const existing = S.snapshots.findIndex(s => s.date === today);
    const vooP = S.prices['VOO']?.price || null; const xu100P = S.prices['XU100']?.price || null;
    // netExtCapital is recorded so volatility and drawdown can be computed on INVESTMENT
    // returns rather than on wealth growth. Without it a payday reads as a large positive
    // return, which inflates volatility and — because deposits only push wealth up —
    // systematically masks real drawdowns. Historic snapshots lack the field, so
    // riskDrawdownVol falls back to the wealth basis and says which one it used.
    // (audit 2026-08-01)
    const snap = { date: today, totalWealth: D.twUSD, portfolioValue: D.gVal, cashUSD: D.totalCashUSD, tradeCost: D.tradeInvHero, tradePnL: D.tPnL, realizedPnL: D.rPnL, invested: D.twInvested, netExtCapital: D.netExtCapital > 0 ? D.netExtCapital : null, fxRate: S.fxRate || 0, benchmarks: { VOO: vooP, XU100: xu100P } };
    if (existing >= 0) S.snapshots[existing] = snap; else S.snapshots.push(snap);
    // ORDER IS LOad-BEARING (audit 2026-08-01). Every consumer treats this as a sorted
    // series: risk-calc.js derives volatility and drawdown from it, and ai-digest.js
    // computes "DAILY CHANGE" from snaps[len-1] vs snaps[len-2]. Live state was found
    // UNSORTED — index 0 held 2026-07-14 while the last element held 2026-07-12 — so the
    // reported daily change was a three-week-old delta and the drawdown was noise.
    // Sorting on write fixes every consumer at once.
    S.snapshots.sort((a, b) => String(a.date).localeCompare(String(b.date)));
    // Keep max 365 days
    if (S.snapshots.length > 365) S.snapshots = S.snapshots.slice(-365);
  }
}


/**
 * renderDash()
 * MAIN DASHBOARD ORCHESTRATOR
 * Computes shared context and renders all sub-components
 */
export function renderDash() {
  // Compute shared context for all sub-functions
  let gInv = 0, gVal = 0, gInvNoPrice = 0;
  S.holdings.forEach(h => {
    const w = hWealthUSD(h);
    if (w.atCost) { gInvNoPrice += w.usd; }        // priced-less, carried at cost and marked
    else if (w.usd) { gVal += w.usd; gInv += hTotUSD(h); }
  });
  const gP = gVal - gInv, gPP = gInv > 0 ? (gP / gInv) * 100 : 0;
  let tPnL = 0;
  S.trades.filter(t => t.status !== 'Closed').forEach(t => { const p = S.prices[t.ticker]; if (!p) return;
    const pl = (t.side === 'Long' ? p.price - t.entry : t.entry - p.price) * t.qty * t.leverage;
    tPnL += toUSD(pl, t.currency || 'USD'); });
  // LEDGER basis — this feeds D.twUSD and the daily snapshot. See computeTotalWealthUSD().
  const totalCashUSD = (S.cash?.USD || 0) + toUSD(S.cash?.TRY || 0, 'TRY') + toUSD(S.cash?.EUR || 0, 'EUR');  // ledger-basis: capital identity, deliberate — see computeTotalWealthUSD
  let rPnL = 0; S.realized.forEach(r => { rPnL += r.pnlUSD || toUSD(r.pnl || 0, r.currency || 'USD'); });
  let tradeInvHero = 0; S.trades.filter(t => t.status !== 'Closed').forEach(t => { tradeInvHero += toUSD(t.entry * t.qty, t.currency || 'USD'); });

  // TD accrued interest (current value − cost basis) — missing from old formula
  let tdAccruedUSD = 0;
  S.trades.filter(t => t.status !== 'Closed' && t.type === 'TimeDeposit' && t.td).forEach(t => {
    const acc = tdAccrued(t.td);
    if (acc && acc.currentValue) {
      const curVal = toUSD(acc.currentValue, t.currency || 'TRY');
      const costVal = toUSD(t.td.principal || 0, t.currency || 'TRY');
      tdAccruedUSD += (curVal - costVal); // only the interest part, cost is already in tradeInvHero
    }
  });

  // Reserve Fund total in USD (multi-currency)
  let rfUSD = 0;
  if (typeof S.reserveFund === 'object') {
    Object.entries(S.reserveFund).forEach(([c, v]) => {
      if (!v) return;
      const tryVal = rfToTRY(v, c);
      rfUSD += S.fxRate ? tryVal / S.fxRate : 0;
    });
  } else {
    rfUSD = S.fxRate ? (S.reserveFund || 0) / S.fxRate : 0;
  }

  const liabUSD = totalLiabilitiesUSD();
  // Single source of truth (same formula as the locals above) — keeps Risk page and dashboard
  // identical and order-independent. audit H-3/H-4.
  const twUSD = computeTotalWealthUSD();
  if (typeof window !== 'undefined') window.__twUSD = twUSD; // shared net-worth for cross-page widgets (Open Risk %)
  const hv = gVal > 0;
  // Total Wealth P&L via netGrowthUSD() (single source, also called live by the Portfolio hero).
  // Falls back to gP+tPnL when no external-capital history exists (pre-tracking states).
  // `reconciled` is the gate. An unreconciled capital figure is not a small error, it is a
  // different quantity wearing the same label — so the fallback is holdings-and-trades P&L,
  // which at least says what it is. (audit 2026-08-02)
  const _ng = netGrowthUSD();
  const _ngOk = !!(_ng && _ng.reconciled && _ng.netExtCapital > 0);
  const netExtCapital = _ngOk ? _ng.netExtCapital : 0;
  const twPnL = _ngOk ? _ng.twPnL : (gP + tPnL);
  const twInvested = _ngOk ? _ng.netExtCapital : (gInv + tradeInvHero);
  const twPP = twInvested > 0 ? (twPnL / twInvested) * 100 : 0;
  // Sanity check: flag anomalous portfolio-level P&L
  if (gInv > 0 && Math.abs(gPP) > 200) {
    console.warn(`[ZAGX-SANITY] Portfolio P&L ${gPP.toFixed(1)}% looks anomalous (inv=$${gInv.toFixed(0)}, val=$${gVal.toFixed(0)}). Check hTotUSD/hVal per holding.`);
  }
  const xirrRate = hv ? calcPortfolioXIRR() : null;
  const xirrPct = xirrRate !== null ? (xirrRate * 100) : null;
  const actTrades = S.trades.filter(t => t.status !== 'Closed');
  let tradeInvUSD = 0; actTrades.forEach(t => { tradeInvUSD += toUSD(t.entry * t.qty, t.currency || 'USD'); });
  const totalInvested = gInv + tradeInvUSD;
  const totalUnreal = gP + tPnL;
  const totalUnrealP = totalInvested > 0 ? (totalUnreal / totalInvested) * 100 : 0;
  const totalPositions = S.holdings.length + actTrades.length;
  const manN = S.holdings.filter(h => PS[h.ticker]?.s === 'm' && !S.prices[h.ticker]).length;

  // Investment P&L: pure returns generated by investments (not deposits/salary/bonus)
  // gP = portfolio unrealized (includes DRIP value since DRIP excluded from gInv)
  // tPnL = trade unrealized, rPnL = realized, tdAccruedUSD = time deposit interest
  const invPnL = gP + tPnL + rPnL + tdAccruedUSD;
  const invBase = gInv + tradeInvHero; // total capital deployed in investments
  const invPnLP = invBase > 0 ? (invPnL / invBase) * 100 : 0;

  const D = { gInv, gVal, gInvNoPrice, gP, gPP, tPnL, totalCashUSD, rPnL, tradeInvHero, twUSD, hv, twPnL, twInvested, twPP, xirrRate, xirrPct, actTrades, manN, totalInvested, totalUnreal, totalUnrealP, totalPositions, tradeInvUSD, liabUSD, rfUSD, tdAccruedUSD, invPnL, invPnLP, netExtCapital,
    // Why the capital figure is or is not usable, carried through so surfaces can say it
    // in words instead of silently printing a dash. (audit 2026-08-02)
    capReconciled: _ngOk, capReasons: (_ng && _ng.reasons) || [], capUnknown: (_ng && _ng.unknown) || [],
    capDoors: (_ng && _ng.doors) || [], capResidual: _ng ? _ng.residualUSD : null };

  // Update top bar metric for dashboard
  const tbm = document.getElementById('tbPageMetric');
  if (tbm) tbm.textContent = D.hv ? fU(D.twUSD) + ' · ' + pc(D.twPnL).replace('pos','↑').replace('neg','↓').replace('neu','—') + ' ' + fP(D.twPP) : '';

  // Call sub-functions in order
  renderActionBell(computeActionItems());   // the wrapper is gone; the bell is not
  if (typeof window.renderCachedInsights === 'function') window.renderCachedInsights();
  renderDigestCard();// async — auto-generates if stale, sessionStorage cached
  _dashSnapshot(D);
  // Goals dash card + milestone check (via window to avoid circular import)
  if (typeof window.renderGoalsDashCard === 'function') window.renderGoalsDashCard();
  if (typeof window.checkMilestones === 'function') window.checkMilestones(D.twUSD);

  // The drift CARD is gone; the DCA lock it sat next to is not. checkDCALock writes
  // S.rebalancing.dcaLocked, which the Rebalancing page and the monthly plan both read.
  try { checkDCALock(); } catch (_e) { /* rebalancing optional */ }

  // AI Side Panel rendering
  _renderSidePanel(D);

  // NEW neon dashboard (redesign port) — populate visible prototype widgets from same data
  try { _dashNeo(D); } catch (e) { console.error('[ZAGX] _dashNeo failed:', e); }
}

/**
 * _dashNeo(D) — redesign port: feeds the visible neon prototype dashboard (#dashNeo).
 * Reuses the exact same computed data (D) + S. Legacy dashboard stays in DOM (hidden)
 * so every existing connection/render keeps working until features are relocated.
 */
export function _dashNeo(D) {
  if (!SPK) return;
  const $ = id => document.getElementById(id);
  try {
    const _twStr = D.hv ? '$' + Math.round(D.twUSD).toLocaleString('en-US') : 'Enter qty & prices';
    if (D.hv && SPK && SPK.odometer) { if (window._twOdo !== _twStr) { window._twOdo = _twStr; SPK.odometer($('neoTw'), _twStr); } } else { $('neoTw').textContent = _twStr; }
    try { renderTape(); } catch (_e) { /* tape */ }
    const pl = $('neoPL'); pl.className = 'delta ' + pc(D.twPnL);
    pl.textContent = D.hv ? `${D.twPnL >= 0 ? '▲' : '▼'} ${fU(D.twPnL)} · ${fP(D.twPP)} net growth` : '—';
  } catch (_e) { /* hero */ }
  // Data-bound widgets must REFRESH every render (price ticks, holding/trade changes, cloud reload).
  // The draw-on animation runs only on the first build (_firstBuild); later renders update data without
  // re-animating (avoids flicker) and SPK cancels prior travel loops (no rAF leak).
  if (!D.hv) return; // wait until holdings are priced before building data visuals
  const _firstBuild = !window._neoBuilt; window._neoBuilt = true;
  try {
    drawWealthChart(_firstBuild);
  } catch (_e) { /* chart */ }
  try {
    // Allocation now splits TOTAL WEALTH (not just holdings) → matches the hero. Folds in open
    // trades (by class), time deposits, cash and reserve. Liabilities excluded (pie can't be negative).
    const COL = { 'US ETF': '#00f0ff', 'US Stock': '#6aa0ff', 'TR Stock': '#ff2bd6', 'TR Fund': '#34e2a0', Commodity: '#e8b84b', Crypto: '#a78bfa', Cash: '#4678ff', 'Time Deposits': '#4DC9F6', Reserve: '#7fe3c0', Other: '#9aa6c7' };
    const cls = h => { const t = h.type, tr = (h.currency === 'TRY') || /garanti|bist|matriks|midas/i.test(h.broker || ''); if (t === 'Crypto') return 'Crypto'; if (t === 'Commodity') return 'Commodity'; if (t === 'Fund') return 'TR Fund'; if (t === 'ETF') return 'US ETF'; if (t === 'Stock') return tr ? 'TR Stock' : 'US Stock'; return 'Other'; };
    const grp = {};
    S.holdings.forEach(h => { const v = hWealthUSD(h).usd; if (!v) return; const c = cls(h); grp[c] = (grp[c] || 0) + v; });
    S.trades.filter(t => t.status !== 'Closed' && t.type !== 'TimeDeposit').forEach(t => {
      const cap = toUSD((t.entry || 0) * (t.qty || 0), t.currency || 'USD');
      const p = S.prices[t.ticker]; const pnl = p ? toUSD((t.side === 'Long' ? p.price - t.entry : t.entry - p.price) * t.qty * (t.leverage || 1), t.currency || 'USD') : 0;
      const c = t.type === 'Crypto' ? 'Crypto' : t.type === 'Commodity' ? 'Commodity' : (t.currency === 'TRY' ? 'TR Stock' : 'US Stock');
      grp[c] = (grp[c] || 0) + cap + pnl;
    });
    let tdVal = 0; S.trades.filter(t => t.status !== 'Closed' && t.type === 'TimeDeposit' && t.td).forEach(t => { const acc = tdAccrued(t.td); tdVal += toUSD(acc && acc.currentValue ? acc.currentValue : (t.td.principal || 0), t.currency || 'TRY'); });
    if (tdVal > 0) grp['Time Deposits'] = tdVal;
    grp.Cash = (grp.Cash || 0) + (D.totalCashUSD || 0);
    if ((D.rfUSD || 0) > 0) grp.Reserve = D.rfUSD;
    const tot = Object.values(grp).reduce((a, b) => a + b, 0) || 1;
    const data = Object.entries(grp).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]).map(([t, v]) => [t, Math.round(v / tot * 100), COL[t] || COL.Other]);
    SPK.donut($('neoDonut'), data, fU(tot), { animate: _firstBuild });
    $('neoLeg').innerHTML = data.map(([n, p, c]) => `<div><i style="background:${c};box-shadow:0 0 8px ${c}"></i>${n}<b>${p}%</b></div>`).join('');
  } catch (_e) { /* donut */ }
  try {
    _needMoves = []; _needUpd = '';
    const moves = [];
    // Deploy — quantified by amount + days left, but ONLY if this month isn't already deployed/reconciled
    const _td = new Date();
    const _curMoKey = _td.toISOString().slice(0, 7);
    const _curMoStatus = ((S.monthlyRates && S.monthlyRates[_curMoKey]) || {}).status;
    const _monthDone = _curMoStatus === 'reconciled' || _curMoStatus === 'completed';
    if (!_monthDone) {
      const mDeploy = (S.goalSettings && S.goalSettings.monthlyContribution) || (S.settings && (S.settings.monthlyDeploy || S.settings.monthlyTarget)) || 0;
      const daysLeft = new Date(_td.getFullYear(), _td.getMonth() + 1, 0).getDate() - _td.getDate();
      moves.push({ tk: null, ac: '#ff8c42', ic: '$', title: 'Deploy this month', impact: mDeploy ? `<b>${fU(mDeploy)}</b> ready · split across brokers` : 'Set your monthly plan', chip: daysLeft + 'd left', cc: '#ff8c42', meter: Math.max(12, 100 - daysLeft * 3), src: 'monthly model', args: '["monthly"]', btn: 'Deploy' });
    }
    // Review — worst holding, quantified
    let worst = null;
    S.holdings.forEach(h => { const v = hVal(h); if (!v.vU) return; const inv = hTotUSD(h); const p = inv > 0 ? ((v.vU - inv) / inv * 100) : 0; if (!worst || p < worst.p) worst = { t: h.ticker, p }; });
    if (worst && worst.p < -10) moves.push({ tk: worst.t, ac: '#ff5470', ic: '▼', title: `Review ${worst.t}`, impact: `Position <b class="neg">${worst.p.toFixed(0)}%</b> · drags your risk score`, chip: worst.p.toFixed(0) + '%', cc: '#ff5470', meter: Math.min(95, Math.abs(worst.p)), src: 'risk engine · concentration', args: '["risk"]', btn: 'Review' });
    // Act — open trade nearest TP1, quantified by gain to lock
    let near = null;
    S.trades.filter(t => t.status !== 'Closed' && t.tp1 && S.prices[t.ticker]).forEach(t => { const p = S.prices[t.ticker].price; const sl = effectiveStop(t); const prox = (t.tp1 - sl) ? ((p - sl) / (t.tp1 - sl)) : 0; if (!near || prox > near.x) near = { t: t.ticker, x: prox, gain: t.entry ? ((t.tp1 - t.entry) / t.entry * 100) : null }; });
    if (near) { const gp = near.gain != null ? `+${near.gain.toFixed(1)}%` : ''; moves.push({ tk: near.t, ac: '#34e2a0', ic: '▲', title: `${near.t} ${near.x >= 0.92 ? 'at TP1' : 'near TP1'}`, impact: `${Math.round(near.x * 100)}% to target${gp ? ` · lock <b class="pos">${gp}</b>` : ''}`, chip: gp || `${Math.round(near.x * 100)}%`, cc: '#34e2a0', meter: Math.min(99, Math.max(5, near.x * 100)), src: 'trade proximity', args: '["trades"]', btn: 'Act' }); }
    while (moves.length < 3) moves.push({ tk: null, filler: true, ac: '#4dc9f6', ic: '◆', title: 'All clear', impact: 'No action needed right now', chip: '', cc: '#4dc9f6', meter: 8, src: '', args: '["dashboard"]', btn: 'View' });
    // AI copilot recommendations take the Moves slots (advisory P0); heuristics above are the
    // fallback / filler when the AI has fewer than 3 non-dismissed recs. All LLM text is esc()'d.
    const _urgOrd = { high: 0, medium: 1, low: 2 };
    const aiRecs = ((S.aiCopilot && S.aiCopilot.recommendations) || []).filter(r => !r.dismissed && !r.acted)
      .sort((a, b) => (_urgOrd[a.urgency] ?? 2) - (_urgOrd[b.urgency] ?? 2)).slice(0, 3);
    const _recCard = r => {
      const t = r.type || 'alert';
      const neg = t === 'sell' || t === 'stop_loss';
      const pos = t === 'buy' || t === 'take_profit' || t === 'scale_in' || t === 'dca_adjust';
      const ac = neg ? '#ff5470' : pos ? '#34e2a0' : (t === 'rebalance' || t === 'hedge') ? '#ff8c42' : '#4dc9f6';
      // LLM output: only the three known urgencies reach the DOM as a chip
      const urg = ['high', 'medium', 'low'].includes(r.urgency) ? r.urgency : 'low';
      const cc = urg === 'high' ? '#ff5470' : urg === 'medium' ? '#e8b84b' : '#4dc9f6';
      const inTrade = r.ticker && S.trades.some(x => x.status !== 'Closed' && x.ticker === r.ticker);
      return { tk: r.ticker || null, ac, ic: neg ? '▼' : pos ? '▲' : (t === 'rebalance' || t === 'hedge') ? '⇄' : '◆',
        title: r.title || `${t} ${r.ticker || ''}`, impact: esc(r.action || r.reasoning || ''),
        chip: urg.toUpperCase(), cc,
        meter: urg === 'high' ? 90 : urg === 'medium' ? 60 : 30,
        recId: r.id || null,
        src: 'AI copilot' + (r.ticker ? ' · ' + r.ticker : ''),
        args: inTrade ? '["trades"]' : '["portfolio"]', btn: 'Review' };
    };
    const finalMoves = [...aiRecs.map(_recCard), ...moves].slice(0, 3);
    _needMoves = finalMoves;
    $('neoMoves').innerHTML = finalMoves.map(m => `<div class="mv" style="--ac:${m.ac}">
      <div style="display:flex;align-items:center;gap:8px"><div class="ic">${m.ic}</div>${m.chip ? `<span class="mvchip" style="color:${m.cc};border-color:${m.cc}55;background:${m.cc}14">${esc(m.chip)}</span>` : ''}</div>
      <div class="ttl">${esc(m.title)}</div>
      <div class="rsn">${m.impact}</div>
      <div class="mvmeter"><div data-w="${m.meter}" style="background:${m.cc}"></div></div>
      ${m.src ? `<div class="mvsrc">◦ ${esc(m.src)}</div>` : ''}
      <button class="go" data-action="navTo" data-args='${m.args}'>${m.btn} →</button>
    </div>`).join('');
    requestAnimationFrame(() => requestAnimationFrame(() => { document.querySelectorAll('#neoMoves .mvmeter > div').forEach(d => { d.style.width = d.dataset.w + '%'; }); }));
    const u = $('neoMovesUpd');
    if (u) u.textContent = aiRecs.length
      ? `AI · scan ${_timeAgo(S.aiCopilot.lastScan && S.aiCopilot.lastScan.ts) || 'done'}`
      : 'heuristic · updated just now';
    if (u) _needUpd = u.textContent;
  } catch (_e) { /* moves */ }
  try {
    const mDeploy = (S.goalSettings && S.goalSettings.monthlyContribution) || (S.settings && (S.settings.monthlyDeploy || S.settings.monthlyTarget)) || (S.cashflow && (S.cashflow.monthlySurplus || S.cashflow.surplus)) || 0;
    const k = [
      ['INV P&L', D.hv ? fU(D.invPnL) : '—', pc(D.invPnL), D.hv ? fP(D.invPnLP) : '', ''],
      ['Monthly Deploy', mDeploy ? fU(mDeploy) : '—', '', mDeploy ? 'on track' : 'set plan', ''],
      ['Reserve Fund', fU(D.rfUSD), '', D.rfUSD > 0 ? '▲ full' : 'empty', 'color:var(--gold)']
    ];
    $('neoKpi').innerHTML = k.map(([l, v, cls, chip, vstyle], i) => `<div class="panel kpi"><div class="top2"><div class="lbl">${l}</div>${chip ? `<div class="chip g">${chip}</div>` : ''}</div><div class="v ${cls}" style="${vstyle}">${v}</div><svg class="spark" id="neoSp${i}"></svg></div>`).join('');
    // Real series from snapshots (no fabricated data): INV P&L = portfolioValue − invested; Monthly Deploy = cumulative invested. Reserve has no history → empty.
    const _sn = S.snapshots || [];
    const invPnLSeries = _sn.map(s => (typeof s.portfolioValue === 'number' && typeof s.invested === 'number') ? s.portfolioValue - s.invested : null).filter(x => x != null);
    const investedSeries = _sn.map(s => typeof s.invested === 'number' ? s.invested : null).filter(x => x != null);
    const kpiSeries = [invPnLSeries, investedSeries, null];
    ['#34e2a0', '#00f0ff', '#e8b84b'].forEach((c, i) => {
      const el = $('neoSp' + i), pts = kpiSeries[i];
      if (!el) return;
      // Reserve has no history BY DESIGN — the null above is deliberate, not a data failure.
      // Handing it to spark() and then reading spark's "no/insufficient data" warning is us
      // warning ourselves about a decision we made, on every render. An empty sparkline is
      // the honest render; the warning is noise, and noise is where real ones hide.
      if (!Array.isArray(pts) || pts.length < 2) { el.innerHTML = ''; return; }
      SPK.spark(el, c, pts, { animate: _firstBuild });
    });
  } catch (_e) { /* kpi */ }
  try { _neoHeatmap(_firstBuild); } catch (_e) { /* heat */ }
  try {
    // Positions that need you — open trades ranked by urgency (proximity to TP1 vs SL)
    const sym = c => c === 'TRY' ? '₺' : c === 'EUR' ? '€' : '$';
    _needRows = [];
    let rows;
    const allRows = S.trades.filter(t => t.status !== 'Closed' && t.type !== 'TimeDeposit').map(t => {
      const p = S.prices[t.ticker]; const price = p ? p.price : null;
      if (price == null || t.tp1 == null) return null;
      const slEff = effectiveStop(t);
      const dom = t.tp1 - slEff;
      const prox = dom ? Math.max(0, Math.min((price - slEff) / dom, 1)) : 0;
      const toTP = price ? ((t.side === 'Long' ? t.tp1 - price : price - t.tp1) / price * 100) : null;
      return { t, price, cur: t.currency || 'USD', prox, toTP };
    }).filter(Boolean).sort((a, b) => b.prox - a.prox);
    _needRows = allRows;   // the merge takes every position; the five-row cut below is this panel's own
    rows = allRows.slice(0, 5);
    $('neoSig').innerHTML = rows.length ? rows.map(r => {
      const near = r.prox >= 0.92; const col = near ? 'var(--grn)' : r.prox < 0.2 ? 'var(--red)' : 'var(--cyan)';
      const lbl = near ? 'at TP1' : (r.toTP != null ? Math.abs(r.toTP).toFixed(1) + '% to TP1' : '');
      const btn = near ? 'Apply TP' : r.prox < 0.2 ? 'Review' : 'Watch';
      const px = r.price >= 1000 ? Math.round(r.price).toLocaleString('en-US') : r.price.toFixed(2);
      return `<div class="posrow">
        <div class="tk">${esc(r.t.ticker.slice(0, 3))}</div>
        <div style="flex:1;min-width:0">
          <div style="display:flex;justify-content:space-between;gap:8px"><b style="font-size:12px;color:var(--t1)">${esc(r.t.ticker)}</b><span style="font-size:11px;color:${col};font-weight:600">${lbl}</span></div>
          <div class="prail"><span class="pcur" data-prox="${r.prox.toFixed(3)}" style="left:0"></span></div>
          <div style="display:flex;justify-content:space-between;align-items:center;margin-top:6px"><span class="sub2">${esc(r.t.side)} · ${sym(r.cur)}${px}</span>
          <button class="posbtn" data-action="navTo" data-args='["trades"]' style="border-color:${col};color:${col}">${btn} →</button></div>
        </div>
      </div>`;
    }).join('') : '<div class="neo-empty">No open trades with a TP set</div>';
    requestAnimationFrame(() => requestAnimationFrame(() => { document.querySelectorAll('#neoSig .pcur').forEach(c => { c.style.left = (parseFloat(c.dataset.prox) * 100) + '%'; }); }));
  } catch (_e) { /* positions */ }
  try { _renderNeed(_needMoves, _needRows, _needUpd); } catch (e) { console.error('[ZAGX] _renderNeed failed:', e); }
  try {
    // My movers — biggest movers among MY instruments TODAY, ranked by $ impact on net worth.
    const sym = c => c === 'TRY' ? '₺' : c === 'EUR' ? '€' : '$';
    const mv = S.holdings.map(h => {
      const v = hVal(h); const p = S.prices[h.ticker];
      if (!v.vU || !p || p.changePct == null) return null;
      return { t: h.ticker, px: p.price, cur: p.cur || 'USD', cp: p.changePct, usd: v.vU * (p.changePct / 100) };
    }).filter(Boolean).sort((a, b) => Math.abs(b.usd) - Math.abs(a.usd)).slice(0, 6);
    $('neoMovers').innerHTML = mv.length ? mv.map(m => {
      const col = m.cp >= 0 ? 'var(--grn)' : 'var(--red)';
      const px = m.px >= 1000 ? Math.round(m.px).toLocaleString('en-US') : m.px.toFixed(m.px < 10 ? 3 : 2);
      return `<div class="pbm" id="nmv_${esc(m.t)}"><span class="livedot"></span>
        <div class="tk">${esc(m.t.slice(0, 3))}</div>
        <div><div class="nm">${esc(m.t)}</div><div class="sub" style="color:${col}">${m.cp >= 0 ? '▲' : '▼'} ${Math.abs(m.cp).toFixed(2)}% <span style="color:var(--t3)">today</span></div></div>
        <div><div class="px">${sym(m.cur)}${px}</div><div class="imp" style="color:${col}">${m.usd >= 0 ? '+' : ''}${fU(m.usd)} <span style="color:var(--t3);font-size:9px">on my position</span></div></div>
      </div>`;
    }).join('') : '<div class="neo-empty">No live price moves yet</div>';
    // honest "ticking": flash a card only when its price actually changed since last render
    mv.forEach(m => {
      const last = _moverLastPx[m.t];
      if (last != null && last !== m.px) {
        const el = document.getElementById('nmv_' + m.t);
        if (el) { const dir = m.px > last ? 'flash-up' : 'flash-down'; el.classList.add(dir); setTimeout(() => { el.classList.remove(dir); }, 600); }
      }
      _moverLastPx[m.t] = m.px;
    });
  } catch (_e) { /* movers */ }
  // one-time entrance: fade-in panels + aurora parallax (guarded so re-renders don't flicker)
  if (!window._neoInit) {
    window._neoInit = true;
    try {
      document.querySelectorAll('#dashNeo .panel').forEach(e => e.classList.add('fade'));
      SPK.reveal(60);
      if (SPK.parallax) SPK.parallax();
    } catch (_e) { /* anim */ }
  }
}

// ═══════ DCA OPPORTUNITIES CARD ═══════
let _dcaExpanded = null; // ticker of expanded row


// ═══════ P4 DASHBOARD: ONE ROW PER POSITION (mockup v1.0, approved 2026-10-17) ═══════
// Today's Moves, Positions that need you and the Copilot side-panel items name the same positions
// three and four times over. This surface renders one row per position with the live rail and
// figure, and folds every other surface's text about that position under it: the Move card
// (title, reasoning, meter, source), the Copilot sentences, the DCA line. The two merged panels
// keep rendering (Neon, parity); the Zen skin hides them and shows this. Which rows are open is
// in-memory: positions change, nothing to remember.
let _needMoves = [], _needRows = [], _needUpd = '';
const _needOpen = new Set();
export function dashNeedToggle(key) {
  if (_needOpen.has(key)) _needOpen.delete(key); else _needOpen.add(key);
  document.querySelectorAll('#neoNeed .nr').forEach(r => { if (r.dataset.key === key) r.classList.toggle('open', _needOpen.has(key)); });
}
export function _renderNeed(moves, rows, upd) {
  const host = document.getElementById('neoNeed'), head = document.getElementById('neoNeedHead');
  if (!host) return;
  const sym = c => c === 'TRY' ? '₺' : c === 'EUR' ? '€' : '$';
  const cp = S.aiCopilot || {};
  // every Copilot line about the position, not the side panel's first four: newest first, one per
  // distinct sentence, three at most. A line names its position by `ticker`, or by a leading word
  // that is an open trade's ticker (matched on the raw text, before escaping).
  const openTk = new Set(S.trades.filter(t => t.status !== 'Closed').map(t => t.ticker));
  const cpItems = [...(cp.alerts || []), ...(cp.recommendations || [])].filter(a => !a.dismissed).map(a => { const raw = String(a.message || a.text || ''); const w = (raw.match(/^([A-Z0-9.]+)\s/) || [])[1]; return { tk: a.ticker || (openTk.has(w) ? w : null), text: esc(raw), ts: a.ts || a.createdAt || 0 }; }).sort((a, b) => (b.ts > a.ts ? 1 : b.ts < a.ts ? -1 : 0));
  let dca = [];
  try { dca = scanDCAOpportunities(); } catch (_e) { /* ok */ }
  const used = new Set();
  const items = rows.map(r => {
    const t = r.t; const tk = t.ticker;
    const slEff = effectiveStop(t);
    // side-aware, like `toTP` above: a Short is past its stop when price is above it
    const buf = (r.price && slEff != null) ? (t.side === 'Short' ? (slEff - r.price) : (r.price - slEff)) / r.price * 100 : null;
    const past = buf != null && buf < 0;
    const mv = moves.find(m => m.tk === tk && !m.filler); if (mv) used.add(mv);
    const seen = new Set(); const cpl = cpItems.filter(c => c.tk === tk && c.text && !seen.has(c.text) && seen.add(c.text)).slice(0, 3);
    // a position past its stop is not an averaging-down candidate; the scan does not know that
    const dl = past ? [] : dca.filter(o => o.ticker === tk).map(o => `${esc(o.ticker)} ${o.pnlPct}% · +$500 → avg -${Math.abs(o.scenarios[1].costDropPct)}%`);
    const near = r.prox >= 0.92;
    const fig = past ? `${Math.abs(buf).toFixed(1)}% beyond stop` : near ? 'at TP1' : (r.toTP != null ? Math.abs(r.toTP).toFixed(1) + '% to TP1' : '');
    const figCls = past ? 'neg' : near ? 'pos' : '';
    const why = mv ? esc(mv.title) : (cpl[0] ? cpl[0].text : (dl[0] ? 'Averaging-down candidate · ' + dl[0].replace(esc(tk) + ' ', '') : (near ? 'At TP1' : 'Approaching TP1')));
    const btn = near ? 'Apply TP' : (past || r.prox < 0.2) ? 'Review' : 'Watch';
    const px = r.price >= 1000 ? Math.round(r.price).toLocaleString('en-US') : r.price.toFixed(2);
    const srcN = 1 + (mv ? 1 : 0) + (cpl.length ? 1 : 0) + (dl.length ? 1 : 0);
    const tp1 = near ? 'at TP1' : (r.toTP != null ? Math.abs(r.toTP).toFixed(1) + '% to TP1' : '');
    return { key: 'p:' + t.id, tk, tp1, buf, chip: tk.slice(0, 3), sub: `${esc(t.side)} · ${sym(r.cur)}${px}`, prox: r.prox, past, fig, figCls, why, mv, cpl, dl, btn, srcN, order: past ? 0 : 1 };
  }).sort((a, b) => (a.order - b.order) || (a.past ? (a.buf - b.buf) : (a.prox - b.prox)));   // deepest breach leads, then the tightest rail
  // a Move about something that is not an open position (Deploy this month, Review <holding>) is its own row
  const orphans = moves.filter(m => !used.has(m) && !m.filler).map(m => ({ key: 'm:' + (m.recId || (m.src + '|' + m.title)), tk: m.tk || '', chip: (m.tk || '◆').slice(0, 3), sub: esc(m.src || ''), why: esc(m.title), mv: m, cpl: [], dl: [], fig: m.chip || '', figCls: '', prox: null, past: false, btn: m.btn || 'Review', args: m.args, srcN: 1 }));
  // keys reach the DOM as data-key / data-args: encoded so a quote in an id or a title cannot break them
  const all = [...items, ...orphans].map(x => ({ ...x, k: encodeURIComponent(x.key) }));
  const nPast = items.filter(x => x.past).length, nDca = items.filter(x => x.dl.length).length;
  if (head) head.innerHTML = `<h2 class="pt">Positions that need you <span class="bdg">${all.length}</span></h2>
    <span class="dn-sum" title="One row per position; every surface that mentions it is folded under">${nPast} past stop · ${nDca} averaging-down</span>
    <span class="dn-upd">${esc(upd || '')}</span>
    <button class="glassbtn" data-action="runCopilotScan">⟳ Scan</button>
    <a class="glassbtn" data-action="navTo" data-args='["trades"]' style="cursor:pointer">Trades →</a>`;
  host.innerHTML = all.length ? all.map(x => `<div class="nr${x.past ? ' alert' : ''}${_needOpen.has(x.k) ? ' open' : ''}" data-key="${x.k}">
      <div class="np" data-action="dashNeedToggle" data-args='["${x.k}"]'><span class="tk">${esc(x.chip)}</span><b>${esc(x.tk)}</b><small>${x.sub}</small></div>
      <div class="nwhy" data-action="dashNeedToggle" data-args='["${x.k}"]'>${x.why}</div>
      <div class="nrail${x.prox == null ? ' none' : ''}">${x.prox != null ? `<i class="c" style="left:${(x.prox * 100).toFixed(1)}%"></i>` : ''}</div>
      <div class="nfig ${x.figCls}">${esc(x.fig)}</div><div class="nsrc" title="surfaces that mention this position">${x.srcN}</div>
      <div class="nx" data-action="dashNeedToggle" data-args='["${x.k}"]' role="button" tabindex="0" aria-label="Details">›</div>
      <div class="nd">
        ${x.mv ? `<div class="ndmv"><div class="ndh"><span class="ndic">${esc(x.mv.ic)}</span>${x.mv.chip ? `<span class="ndchip">${esc(x.mv.chip)}</span>` : ''}<b>${esc(x.mv.title)}</b></div><p>${x.mv.impact}</p><div class="ndmeter"><i style="width:${Number(x.mv.meter) || 0}%"></i></div>${x.mv.src ? `<div class="ndsrc">◦ ${esc(x.mv.src)}</div>` : ''}</div>` : ''}
        ${x.cpl.map(c => `<div class="ndline"><em>Copilot</em>${c.text}</div>`).join('')}
        ${x.dl.map(d => `<div class="ndline"><em>DCA</em>${d}</div>`).join('')}
        ${x.past && x.tp1 ? `<div class="ndline"><em>TP1</em>${esc(x.tp1)}</div>` : ''}
        <div class="ndacts"><button class="glassbtn" data-action="navTo" data-args='${x.args || '["trades"]'}'>${esc(x.btn)} →</button></div>
      </div></div>`).join('') : '<div class="neo-empty">No open trades with a TP set</div>';
}

// ═══════ AI SIDE PANEL RENDERING ═══════
function _renderSidePanel(D) {
  // Copilot alerts + DCA opportunities
  const alertsEl = document.getElementById('copilotPanelAlerts');
  const tsEl = document.getElementById('copilotPanelTs');
  if (alertsEl) {
    const cp = S.aiCopilot || {};
    const alerts = cp.alerts || [];
    const recs = cp.recommendations || [];
    const items = [...alerts, ...recs].slice(0, 4);

    // DCA opportunity summary for side panel
    let dcaOpps = [];
    try { dcaOpps = scanDCAOpportunities(); } catch (_e) { /* ok */ }
    let panelHTML = '';

    if (dcaOpps.length) {
      panelHTML += `<div style="font-size:12px;color:var(--t2);padding:8px 10px;background:rgba(255,107,138,.06);border-radius:6px;border-left:2px solid var(--red);margin-bottom:6px;line-height:1.4;cursor:pointer" data-action="navTo" data-args='["dashboard"]'>
        <div style="font-weight:600;color:var(--red);font-size:11px;margin-bottom:2px">▼ ${dcaOpps.length} DCA Opportunit${dcaOpps.length > 1 ? 'ies' : 'y'}</div>
        ${dcaOpps.slice(0, 3).map(o => `<div style="font-size:11px;color:var(--t3)">${esc(o.ticker)} <span style="color:var(--red);font-weight:600">${o.pnlPct}%</span> · +$500 → avg -${Math.abs(o.scenarios[1].costDropPct)}%</div>`).join('')}
      </div>`;
    }

    if (items.length) {
      panelHTML += items.map(a => {
        const borderColor = a.severity === 'high' || a.type === 'warning' ? 'var(--red)' : a.severity === 'medium' ? 'var(--yel)' : 'var(--grn)';
        return `<div style="font-size:12px;color:var(--t2);padding:7px 10px;background:rgba(255,255,255,.02);border-radius:6px;border-left:2px solid ${borderColor};margin-bottom:4px;line-height:1.4">${esc(a.message || a.text || '')}</div>`;
      }).join('');
    } else if (!dcaOpps.length) {
      panelHTML += '<div class="neo-empty">No active alerts</div>';
    }
    alertsEl.innerHTML = panelHTML;
    if (tsEl) tsEl.textContent = cp.lastScanTs ? new Date(cp.lastScanTs).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
  }

  // Scenarios
  const scEl = document.getElementById('copilotPanelScenarios');
  if (scEl && typeof window.getScenarios === 'function') {
    const scenarios = window.getScenarios();
    if (scenarios.length) {
      scEl.innerHTML = scenarios.slice(0, 4).map(s => {
        const isOn = s.enabled !== false;
        return `<div style="font-size:12px;padding:6px 8px;background:rgba(255,255,255,.02);border-radius:5px;display:flex;align-items:center;gap:6px;margin-bottom:3px">
          <div style="width:7px;height:7px;border-radius:50%;background:${isOn ? 'var(--grn)' : 'var(--t3)'}"></div>
          <span style="flex:1;color:var(--t2)">${esc(s.name)}</span>
          <span style="font-size:10px;padding:2px 6px;border-radius:3px;font-weight:700;background:${isOn ? 'rgba(110,238,180,.1)' : 'rgba(255,255,255,.03)'};color:${isOn ? 'var(--grn)' : 'var(--t3)'}">${isOn ? 'ON' : 'OFF'}</span>
        </div>`;
      }).join('');
    } else {
      scEl.innerHTML = '<div class="neo-empty">No scenarios configured</div>';
    }
  }

  // Cash summary
  const cashEl = document.getElementById('cashPanelSummary');
  if (cashEl) {
    const usd = S.cash?.USD || 0;
    const tryV = cashTRYNow();  // NOW, not the last bank reading
    const eur = S.cash?.EUR || 0;
    cashEl.innerHTML = `
      <div style="display:flex;justify-content:space-between;padding:4px 0;border-bottom:1px solid var(--bd);font-size:12px"><span style="color:var(--t3)">USD</span><span style="font-weight:600;color:var(--grn)">${fU(usd)}</span></div>
      <div style="display:flex;justify-content:space-between;padding:4px 0;border-bottom:1px solid var(--bd);font-size:12px"><span style="color:var(--t3)">TRY</span><span style="font-weight:600;color:var(--yel)">${fT(tryV)}</span></div>
      <div style="display:flex;justify-content:space-between;padding:4px 0;font-size:12px"><span style="color:var(--t3)">EUR</span><span style="font-weight:600;color:var(--sp-cash)">€${f(eur)}</span></div>
      <div style="margin-top:6px"><div style="display:flex;justify-content:space-between;font-size:11px"><span style="color:var(--t3)">Reserve coverage</span><span style="font-weight:600;color:var(--grn)">${(() => { const cov = reserveCoverageUSD(), tgt = reserveTargetUSD(); return cov > 0 && tgt > 0 ? Math.round(cov / tgt * 100) : 0; })()}%</span></div>
      <div style="height:5px;background:var(--bd);border-radius:3px;overflow:hidden;margin:4px 0"><div style="width:${(() => { const cov = reserveCoverageUSD(), tgt = reserveTargetUSD(); return cov > 0 && tgt > 0 ? Math.min(Math.round(cov / tgt * 100), 100) : 0; })()}%;height:100%;border-radius:3px;background:var(--grn)"></div></div></div>`;
  }

  // Allocation bar
  const allocEl = document.getElementById('allocPanelBar');
  if (allocEl && D.gVal > 0) {
    const types = {};
    S.holdings.forEach(h => {
      const v = hVal(h);
      if (!v.vU) return;
      const t = h.type || 'Other';
      types[t] = (types[t] || 0) + v.vU;
    });
    const total = Object.values(types).reduce((s, v) => s + v, 0);
    const colors = { 'US Equity': 'var(--grn)', 'US ETF': 'var(--grn)', Crypto: 'var(--yel)', 'BIST Equity': 'var(--ora)', 'BIST Fund': 'var(--ora)', Commodity: 'var(--sp-cash)', 'Time Deposit': 'var(--acc2)', Cash: 'var(--acc2)' };
    const sorted = Object.entries(types).sort((a, b) => b[1] - a[1]);
    const barSegs = sorted.map(([t, v]) => `<div style="flex:${Math.round((v / total) * 100)};height:100%;background:${colors[t] || 'var(--t3)'}"></div>`).join('');
    const legend = sorted.map(([t, v]) => `<span style="display:flex;align-items:center;gap:4px;color:var(--t3)"><span style="width:7px;height:7px;border-radius:2px;background:${colors[t] || 'var(--t3)'}"></span>${t.replace('US ','').replace('BIST ','')} ${Math.round((v / total) * 100)}%</span>`).join('');
    allocEl.innerHTML = `<div style="display:flex;height:8px;border-radius:4px;overflow:hidden;gap:1px">${barSegs}</div><div style="display:flex;flex-wrap:wrap;gap:8px;margin-top:6px;font-size:11px">${legend}</div>`;
  }

  // FX exposure
  const fxEl = document.getElementById('fxPanelExposure');
  if (fxEl && D.gVal > 0) {
    const currs = {};
    S.holdings.forEach(h => {
      const v = hVal(h);
      if (!v.vU) return;
      const c = h.currency || 'USD';
      currs[c] = (currs[c] || 0) + v.vU;
    });
    const total = Object.values(currs).reduce((s, v) => s + v, 0);
    const colorMap = { USD: 'var(--grn)', TRY: 'var(--yel)', EUR: 'var(--sp-cash)' };
    fxEl.innerHTML = Object.entries(currs).sort((a, b) => b[1] - a[1]).map(([c, v]) => {
      const pct = Math.round((v / total) * 100);
      return `<div style="display:flex;justify-content:space-between;font-size:12px;padding:3px 0"><span style="color:var(--t3)">${c} assets</span><span style="font-weight:600;color:${colorMap[c] || 'var(--t2)'}">${pct}%</span></div>`;
    }).join('');
  }
}
